import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { purchaseControllerGetAlbumDownloadNew } from "@/lib/lutely-api/gen/purchase/purchase";
import { toast } from "react-toastify";
import { downloadFileFromUrl } from "./file.utils";

/**
 * Downloads a purchased album.
 *
 * The API builds the ZIP into S3 and returns a signed URL, so the browser's own
 * download manager performs the transfer. Buffering the archive here instead —
 * `arraybuffer`, then a `Blob` copy, then a synthetic `<a download>` on a
 * `blob:` URL — lost roughly one in ten downloads on mobile, and saved nothing
 * at all inside in-app browsers (Instagram, X, Facebook), which have no download
 * manager to hand a blob URL to. A signed URL is also resumable, because S3
 * honours range requests.
 *
 * The filename arrives in the object's `Content-Disposition` header rather than
 * being built here: a `download` attribute is ignored on a cross-origin URL, so
 * that header is the only thing that both forces the download and names it.
 */
export async function downloadAlbum(
  albumOrderId: string,
  options?: { onError?: (error: unknown) => void },
): Promise<void> {
  try {
    const { presignedUrl } =
      await purchaseControllerGetAlbumDownloadNew(albumOrderId);

    if (!presignedUrl) {
      throw new Error("This album isn't available to download right now.");
    }

    downloadFileFromUrl(presignedUrl);
  } catch (error: unknown) {
    toast.error(getErrorMessage(error));
    options?.onError?.(error);
    throw error;
  }
}
